import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import { adminDailyPromptDeleteAction, adminDailyPromptsListAction } from "../Action/action";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function plainTextFromHtml(html) {
  return String(html || "")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function truncate50(htmlOrText) {
  const t = plainTextFromHtml(htmlOrText);
  if (t.length <= 50) return t || "—";
  return `${t.slice(0, 50)}…`;
}

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function DailyPrompts() {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 50 });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [perPage]);

  useEffect(() => {
    let cancelled = false;
    const searchParam = query.trim() ? query.trim() : undefined;

    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminDailyPromptsListAction({
          page,
          per_page: perPage,
          search: searchParam
        });
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setMeta({ total: 0, page, per_page: perPage });
          setError(res?.message || res?.msg || "Failed to load daily prompts.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setMeta({ total: 0, page, per_page: perPage });
        setError(e?.response?.data?.message || e?.message || "Failed to load daily prompts.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [page, perPage, query, reloadNonce]);

  const total = Number(meta?.total ?? 0);
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1;

  useEffect(() => {
    if (loading) return;
    if (page > totalPages) setPage(totalPages);
  }, [loading, page, totalPages]);

  const viewRows = useMemo(() => {
    return rows.map((r) => ({
      raw: r,
      id: r?.id,
      weekName: r?.week_name || "—",
      title: r?.title || "—",
      shortPreview: truncate50(r?.short_description),
      day: r?.day != null ? String(r.day) : "—",
      displayOrder: r?.display_order != null && r?.display_order !== "" ? String(r.display_order) : "0",
      created: formatDate(r?.created_on)
    }));
  }, [rows]);

  const start = total > 0 && viewRows.length > 0 ? (page - 1) * perPage + 1 : 0;
  const end = total > 0 && viewRows.length > 0 ? start + viewRows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminDailyPromptDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Delete failed");
      toast.success("Daily prompt deleted", { toastId: "daily-prompt-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error(e?.message || "Delete failed", { toastId: "daily-prompt-delete" });
    } finally {
      setDeleting(false);
    }
  };

  const clearAndReload = () => {
    setQuery("");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Daily prompts unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by title..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="filter-group" style={{ alignItems: "center" }}>
            <span className="small-label" style={{ marginRight: 8 }}>
              Per page
            </span>
            <select
              className="form-control"
              style={{ width: "auto", minWidth: 72, padding: "6px 10px" }}
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              disabled={loading}
            >
              {PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>
        <Link to="/daily-prompts/new" className="btn btn-primary">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
          Add Daily Prompt
        </Link>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Week</th>
                <th>Day</th>
                <th>Display Order</th>
                <th>Title</th>
                <th>Short description</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : (
                viewRows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.weekName}</strong>
                    </td>
                    <td>
                      <span className="badge badge-info">{r.day}</span>
                    </td>
                    <td>{r.displayOrder}</td>
                    <td>{r.title}</td>
                    <td style={{ maxWidth: 320 }}>{r.shortPreview}</td>
                    <td>{r.created}</td>
                    <td>
                      <div className="actions-cell">
                        <Link to={`/daily-prompts/${r.id}/edit`} className="action-link">
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="action-link text-danger"
                          onClick={() => openDelete(r.raw)}
                          style={{ background: "transparent", border: "none", padding: 0 }}
                        >
                          Delete
                        </button>
                    
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!loading && viewRows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-icon">🫥</div>
                      No daily prompts found.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            {total === 0
              ? "No prompts"
              : `Showing ${start}–${end} of ${total} Prompt${total === 1 ? "" : "s"} · Page ${page} of ${totalPages}`}
          </span>
          {total > 0 ? (
            <div className="pagination-pages">
              <button
                type="button"
                title="First page"
                onClick={() => setPage(1)}
                disabled={page <= 1 || loading}
              >{"<<"}
              </button>
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
                ‹
              </button>
              {pageButtons.map((p) => (
                <button
                  key={p}
                  className={p === page ? "active" : ""}
                  type="button"
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                ›
              </button>
              <button type="button" title="Last page" onClick={() => setPage(totalPages)} disabled={page >= totalPages || loading}>{">>"}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="daily prompt"
        itemName={deleteRow?.title || "Daily prompt"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}
